/**
 * Connection content component - displays connection details and settings
 * Used in both ConnectionPane (sidebar) and connection pages
 * Supports connection configuration, capabilities, triggers, and management
 */

import {
  getConnectionSettings,
  getUserConfig,
  IntegrationEnhanced,
  useDeleteOrgIntegrationConnection,
  useOrgIntegrationConnections,
  useOrgIntegrations,
  useUpdateIntegrationConnection,
} from "@/api/integrations";
import { useUserInfo } from "@/api/user";
import {
  LcBadge,
  outlineDestructiveClassName,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import {
  CliConnectorOverview,
  SectionHead,
} from "@/components/ConnectionContent/CliConnectorOverview";
import { getCliConnectorContent } from "@/components/ConnectionContent/cliConnectorContent";
import { ConnectionSetupWizard } from "@/components/ConnectionSetupWizard";
import { IntegrationCapabilities } from "@/components/IntegrationCapabilities";
import { IntegrationTriggers } from "@/components/IntegrationTriggers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useSettingsInDialog } from "@/context/SettingsInDialogContext";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useConnectIntegration } from "@/hooks/useConnectIntegration";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { IntegrationIcon } from "@cohesive/ui-components";
import { Building2, Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

interface ConnectionContentProps {
  integrationId: string;
  context: "page" | "pane";
  showHeader?: boolean;
}

export const ConnectionContent: React.FC<ConnectionContentProps> = ({
  integrationId,
  context,
  showHeader = true,
}) => {
  const appNavigate = useAppNavigate();
  const openSettings = useOpenSettings();
  const isInDialog = useSettingsInDialog();
  const deleteIntegrationConnection = useDeleteOrgIntegrationConnection();
  const { mutateAsync: connectIntegration } = useConnectIntegration();

  const isOrgAdmin = useIsOrgAdmin();
  const { data: userInfo } = useUserInfo();

  const { data: integrations, isLoading: integrationsLoading } =
    useOrgIntegrations();
  const { data: connections, isLoading: connectionsLoading } =
    useOrgIntegrationConnections();

  const integration = integrations?.find(
    (integration) => integration.id === integrationId,
  );

  const connection = connections?.find(
    (connection) => connection.integration_id === integrationId,
  );

  const canManageIntegration = isOrgAdmin || integration?.auth_level === "user";

  // Check if schema has any configurable fields
  const hasConfigurableFields = useMemo(() => {
    const schema = integration?.configuration_schema;
    if (!schema) return false;

    const hasSettings =
      schema.settings?.schema?.properties &&
      Object.keys(schema.settings.schema.properties).length > 0;
    const hasUserSettings =
      schema.user_settings?.schema?.properties &&
      Object.keys(schema.user_settings.schema.properties).length > 0;

    return hasSettings || hasUserSettings;
  }, [integration?.configuration_schema]);

  // Check if connection needs setup based on configuration schema
  const needsSetup = useMemo(() => {
    if (
      !connection ||
      !integration?.configuration_schema ||
      !hasConfigurableFields ||
      !userInfo?.user?.id
    ) {
      return false;
    }

    const settings = getConnectionSettings(connection);
    const userConfig = getUserConfig(connection, userInfo.user.id);

    // If user has dismissed the setup wizard, don't show it again
    if (settings?._setup_dismissed || userConfig?._setup_dismissed) {
      return false;
    }

    // Show wizard if no config has been saved yet (even for optional fields)
    const hasSettings = settings && Object.keys(settings).length > 0;
    const hasUserConfig = userConfig && Object.keys(userConfig).length > 0;

    return !hasSettings && !hasUserConfig;
  }, [connection, integration, hasConfigurableFields, userInfo]);

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [instructions, setInstructions] = useState(
    connection?.instructions || "",
  );
  const updateMutation = useUpdateIntegrationConnection();

  // Sync instructions state when connection changes
  useEffect(() => {
    setInstructions(connection?.instructions || "");
  }, [connection?.id, connection?.instructions]);

  const providedByCohesive = integration?.provider === "cohesive";

  const handleSaveInstructions = () => {
    if (!connection) return;
    updateMutation.mutate(
      {
        integrationConnectionId: connection.id,
        instructions,
        integration: integration!,
      },
      {
        onSuccess: () => {
          toast.success("Instructions updated successfully");

          analytics.trackSaveIntegrationConnectionInstructions({
            integrationConnectionId: connection.id,
            instructionsLength: instructions.length,
          });
        },
        onError: () => {
          toast.error("Failed to update instructions");
        },
      },
    );
  };

  const handleIntegrationConnect = async (integration: IntegrationEnhanced) => {
    try {
      await connectIntegration(integration);
      toast.success("Integration connected successfully");
    } catch (error) {
      toast.error("Failed to connect integration");
    }
  };

  const handleDisconnectSuccess = () => {
    if (context === "pane" && !isInDialog) {
      appNavigate({
        pane: "rightPane",
        to: null,
      });
    }
    openSettings(ROUTES.SETTINGS.CONNECTORS);
  };

  if (connectionsLoading || integrationsLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-neutral-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Connection not found</h2>
        <p className="text-muted-foreground">
          The requested connection could not be found.
        </p>
      </div>
    );
  }

  // Show setup wizard if connection needs configuration
  if (connection && needsSetup && hasConfigurableFields) {
    return (
      <div className={cn("space-y-6 @container", context === "pane" && "pb-6")}>
        {showHeader && (
          <>
            {/* Same masthead as the connected view — the setup branch used to
                run its own copy of the old header, so a connector changed
                shape halfway through being set up. */}
            <div className="flex items-center gap-3">
              <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05">
                <IntegrationIcon
                  platform={integration.platform}
                  name={integration.name}
                  imageURL={integration.metadata?.image_url}
                  className="size-6"
                />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-medium text-n-90">
                  {integration.name}
                </h3>
                <p className="truncate text-sm text-n-50">
                  Finish setup to start using this connector
                </p>
              </div>
            </div>
          </>
        )}
        <ConnectionSetupWizard
          connection={connection}
          schema={integration.configuration_schema!}
          authLevel={integration.auth_level || "organization"}
          onComplete={() => {
            // Wizard handles toast and query invalidation
          }}
        />
      </div>
    );
  }

  const title = `${integration.name}`;

  // CLI-backed toolsets render an informational/capability view instead of the
  // per-tool action and per-trigger card lists.
  const isCliConnector = !!integration.cli_name;
  const { tagline } = getCliConnectorContent(integration);

  return (
    <div className={cn("space-y-6 @container", context === "pane" && "pb-6")}>
      {showHeader && (
        <>
          {/* The design system's detail masthead: a 48px bordered tile, the
              name at text-lg, and the one governing verb on the right. The
              old IndexLayout head ran a ~60px icon on `bg-muted` under an
              `h1 text-3xl font-bold` — three registers louder than any other
              pane in the modal. */}
          <div className="flex items-center gap-3">
            <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05">
              <IntegrationIcon
                platform={integration.platform}
                name={integration.name}
                imageURL={integration.metadata?.image_url}
                className="size-6"
              />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-medium text-n-90">
                {title}
              </h3>
              {/* The connector's own one-line tagline, truncated. Reads the
                  same connected or not, so it can't contradict the verb beside
                  it. Falls back to the scope when the tagline is just the
                  description we already render below. */}
              <p className="truncate text-sm text-n-50">
                {tagline !== integration.description
                  ? tagline
                  : integration.auth_level === "organization"
                    ? "Organization-wide connector"
                    : "Connects to your account"}
              </p>
            </div>
            {/* Disconnect lives here now, not in a danger zone at the foot:
                it is the only destructive verb on this page, and the design
                system puts a page's governing action beside its title. */}
            {connection ? (
              canManageIntegration && !providedByCohesive ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "shrink-0",
                    smButtonClassName,
                    outlineDestructiveClassName,
                  )}
                  onClick={() => setIsDisconnectModalOpen(true)}
                >
                  Disconnect
                </Button>
              ) : (
                <LcBadge>Connected</LcBadge>
              )
            ) : !providedByCohesive ? (
              canManageIntegration ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className={cn("shrink-0", smButtonClassName)}
                  onClick={() => {
                    handleIntegrationConnect(integration);
                  }}
                >
                  Connect
                </Button>
              ) : (
                <span className="shrink-0 text-sm text-n-50">
                  Ask an admin to connect
                </span>
              )
            ) : null}
          </div>
          {integration.auth_level === "organization" && (
            <div className="rounded-md border border-spectrum-blue-light bg-spectrum-blue-light/15 p-4 my-4 text-sm text-teal dark:border-spectrum-blue-dark dark:bg-spectrum-blue-dark/20/30 dark:text-teal">
              <div className="flex items-start space-x-3">
                <Building2 className="h-5 w-5 flex-shrink-0 text-teal dark:text-teal mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">
                    Organization-Wide Connector
                  </p>
                  <p>
                    {connection
                      ? "This connector is available to all members of your organization to use in their agents and workflows."
                      : "Heads up! This is an organization-wide connector. When connected, this integration will be available for all members of your organization to use in their agents and workflows."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!canManageIntegration && !providedByCohesive && (
            <div className="rounded-md border border-spectrum-blue-light bg-spectrum-blue-light/15 p-4 my-4 text-sm text-teal">
              <div className="flex items-start space-x-2">
                <Info className="h-5 w-5 flex-shrink-0 text-teal mt-0.5" />
                <div>
                  <p className="font-medium">Organization-Wide Connector</p>
                  <p className="mt-1">
                    This connector can only be managed by organization
                    administrators. Please contact your administrator to make
                    changes.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {integration &&
        (isCliConnector ? (
          <CliConnectorOverview
            integration={integration}
            connected={!!connection}
          />
        ) : (
          <>
            <IntegrationCapabilities
              toolSetID={integration.tool_set_id}
              integrationName={integration.name}
            />
            <IntegrationTriggers
              toolSetID={integration.tool_set_id}
              integrationName={integration.name}
            />
          </>
        ))}

      {connection &&
        canManageIntegration &&
        hasConfigurableFields &&
        integration.configuration_schema && (
          <ConnectionSetupWizard
            connection={connection}
            schema={integration.configuration_schema}
            authLevel={integration.auth_level || "organization"}
            mode="manage"
          />
        )}

      {connection && canManageIntegration && (
        // The pane's section grammar rather than a Card: mono group label,
        // the field, and a Save that appears only while the text differs from
        // what's stored — the same quiet-dirty rule DirtyField uses.
        <section className="flex flex-col gap-1.5">
          <SectionHead title="Custom instructions" />
          <p className="max-w-measure text-sm text-n-50">
            Extra context the agent uses whenever this connector is called.
          </p>
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            className="mt-1 min-h-28 max-w-measure resize-y border-input bg-input-background text-base placeholder:text-n-40"
            placeholder="Always use the team's shared calendar…"
          />
          {instructions !== (connection.instructions ?? "") ? (
            <div className="mt-1 flex">
              <Button
                size="sm"
                variant="secondary"
                className={smButtonClassName}
                disabled={updateMutation.isPending}
                isLoading={updateMutation.isPending}
                onClick={handleSaveInstructions}
              >
                Save
              </Button>
            </div>
          ) : null}
        </section>
      )}

      {connection && canManageIntegration && !providedByCohesive && (
        <>
          <Dialog
            open={isDisconnectModalOpen}
            onOpenChange={setIsDisconnectModalOpen}
          >
            <DialogContent overlayClassName="z-[1000]" className="z-[1000]">
              <DialogHeader>
                <DialogTitle>Disconnect connector</DialogTitle>
                <DialogDescription>
                  Are you sure you want to disconnect{" "}
                  {connection.integration_name}? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDisconnectModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteIntegrationConnection.mutate(
                      {
                        integrationConnection: connection,
                        integration: integration,
                      },
                      {
                        onSuccess: () => {
                          toast.success(
                            `${connection.integration_name} has been disconnected`,
                          );
                          handleDisconnectSuccess();
                        },
                        onError: () => {
                          toast.error(
                            `Failed to disconnect ${connection.integration_name}`,
                          );
                        },
                      },
                    );
                    setIsDisconnectModalOpen(false);
                  }}
                >
                  Disconnect
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
